
import React from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Loader2, Mail, Lock, Eye, EyeOff } from "lucide-react";
import { Separator } from "@/components/ui/separator";

interface LoginFormProps {
  email: string;
  password: string;
  loading: boolean;
  onEmailChange: (val: string) => void;
  onPasswordChange: (val: string) => void;
  onSubmit: (e: React.FormEvent) => void;
  onGoogleLogin?: (e: React.MouseEvent) => void;
}

const LoginForm: React.FC<LoginFormProps> = ({
  email,
  password,
  loading,
  onEmailChange,
  onPasswordChange,
  onSubmit,
  onGoogleLogin,
}) => {
  const [showPassword, setShowPassword] = React.useState(false);
  
  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onEmailChange(e.target.value);
  };
  
  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onPasswordChange(e.target.value);
  };
  
  return (
    <form onSubmit={onSubmit} className="space-y-4">
      {/* Email Input */}
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <Mail className="h-5 w-5 text-white/40" />
        </div>
        <Input
          autoFocus
          type="email"
          placeholder="Email"
          value={email}
          autoComplete="username"
          onChange={handleEmailChange}
          disabled={loading}
          required
          className="pl-10 bg-white/10 border-white/10 text-white placeholder:text-white/40 focus:border-[#00B6F0]/50 focus:ring-1 focus:ring-[#00B6F0]/50"
        />
      </div>
      
      {/* Password Input */}
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <Lock className="h-5 w-5 text-white/40" />
        </div>
        <Input
          type={showPassword ? "text" : "password"}
          placeholder="Senha"
          value={password}
          autoComplete="current-password"
          onChange={handlePasswordChange}
          disabled={loading}
          required
          className="pl-10 pr-10 bg-white/10 border-white/10 text-white placeholder:text-white/40 focus:border-[#00B6F0]/50 focus:ring-1 focus:ring-[#00B6F0]/50"
        />
        <div 
          className="absolute inset-y-0 right-0 pr-3 flex items-center cursor-pointer"
          onClick={() => setShowPassword(!showPassword)}
        >
          {showPassword ? (
            <EyeOff className="h-5 w-5 text-white/40 hover:text-white/60" />
          ) : (
            <Eye className="h-5 w-5 text-white/40 hover:text-white/60" />
          )}
        </div>
      </div>
      
      {/* Submit Button */}
      <Button 
        type="submit" 
        className="w-full bg-[#00B6F0] hover:bg-[#00B6F0]/90 text-white shadow-lg shadow-[#00B6F0]/20 rounded-xl mt-2" 
        disabled={loading}
      >
        {loading ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Entrando...
          </>
        ) : (
          "Entrar"
        )}
      </Button>

      {/* Google Login */}
      {onGoogleLogin && (
        <>
          <div className="relative flex items-center justify-center mt-4">
            <Separator className="absolute w-full bg-white/20" />
            <span className="relative px-2 text-xs text-white/40 bg-[#0f141c]">ou</span>
          </div>

          <Button 
            type="button"
            onClick={onGoogleLogin}
            className="w-full bg-white hover:bg-white/90 text-gray-800 shadow-lg rounded-xl flex items-center justify-center gap-2"
            disabled={loading}
          >
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <>
                <svg viewBox="0 0 24 24" width="16" height="16" xmlns="http://www.w3.org/2000/svg">
                  <g transform="matrix(1, 0, 0, 1, 0, 0)">
                    <path d="M21.35,11.1H12.18V13.83H18.69C18.36,17.64 15.19,19.27 12.19,19.27C8.36,19.27 5,16.25 5,12C5,7.9 8.2,4.73 12.2,4.73C15.29,4.73 17.1,6.7 17.1,6.7L19,4.72C19,4.72 16.56,2 12.1,2C6.42,2 2.03,6.8 2.03,12C2.03,17.05 6.16,22 12.25,22C17.6,22 21.5,18.33 21.5,12.91C21.5,11.76 21.35,11.1 21.35,11.1V11.1Z" fill="#4285F4" />
                  </g>
                </svg>
                Entrar com Google
              </>
            )}
          </Button>
        </>
      )}
    </form>
  );
};

export default LoginForm;
